import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { AuthState, AuthContextType, UserInfo, TokenPayload } from '../types/auth';
import * as AuthService from '../services/AuthService';
import { setCurrentUserId } from '../services/SaveService';

const STORAGE_KEY = 'authData';

// 从本地存储加载认证数据
const loadAuthFromStorage = (): { 
  token: string | null; 
  user: UserInfo | null 
} => {
  try {
    const storedData = localStorage.getItem(STORAGE_KEY);
    if (!storedData) return { token: null, user: null };
    
    const { token, user, expiresAt } = JSON.parse(storedData);
    
    // 检查token是否过期
    if (expiresAt && Date.now() > expiresAt) {
      localStorage.removeItem(STORAGE_KEY);
      return { token: null, user: null };
    }
    
    return { token, user };
  } catch (error) {
    console.error('从本地存储加载认证数据失败:', error);
    return { token: null, user: null };
  }
};

// 保存认证数据到本地存储
const saveAuthToStorage = (token: string, user: UserInfo) => {
  try {
    const payload = AuthService.parseJwt(token);
    const expiresAt = Date.now() + 2 * 60 * 60 * 1000; // 本地存储2小时后过期
    
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ 
        token, 
        user,
        expiresAt
      })
    );
  } catch (error) {
    console.error('保存认证数据到本地存储失败:', error);
  }
};

// 创建上下文
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// 提供者组件
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    token: null,
    error: null,
    lastChecked: undefined
  });

  // 更新状态 - 使用useCallback记忆化以避免不必要的重新渲染
  const updateState = useCallback((updates: Partial<AuthState> | ((prevState: AuthState) => Partial<AuthState>)) => {
    setState(prev => {
      const updatesObj = typeof updates === 'function' ? updates(prev) : updates;
      return { ...prev, ...updatesObj };
    });
  }, []);

  // 初始化认证状态
  const initializeAuth = useCallback(async () => {
    try {
      updateState({ isLoading: true });
      
      // 1. 从URL参数获取token
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');
      
      if (tokenFromUrl) {
        try {
          // 确保正确解码URL编码的token
          const decodedToken = decodeURIComponent(tokenFromUrl);
          console.log('正在验证解码后的token');
          
          // 从URL获取到token，进行验证
          const { isValid, userInfo, error } = await AuthService.validateToken(decodedToken);
          
          if (isValid && userInfo) {
            // 保存到本地存储
            saveAuthToStorage(decodedToken, userInfo);
            
            // 设置当前用户ID用于代码保存和获取
            console.log('设置用户ID:', userInfo.userId);
            setCurrentUserId(userInfo.userId);
            
            // 更新状态
            updateState({
              isAuthenticated: true,
              isLoading: false,
              user: userInfo,
              token: decodedToken,
              error: null,
              lastChecked: Date.now()
            });
            
            // 从URL中移除token
            urlParams.delete('token');
            const newUrl = window.location.pathname + 
                          (urlParams.toString() ? `?${urlParams.toString()}` : '') + 
                          window.location.hash;
            window.history.replaceState({}, document.title, newUrl);
            
            return;
          } else {
            console.error('URL中的token验证失败:', error);
          }
        } catch (decodeError) {
          console.error('Token解码失败:', decodeError);
        }
      }
      
      // 2. 尝试从本地存储加载
      const { token: storedToken, user: storedUser } = loadAuthFromStorage();
      
      if (storedToken && storedUser) {
        // 验证本地存储的token
        const { isValid, userInfo, error } = await AuthService.validateToken(storedToken);
        
        if (isValid && userInfo) {
          // 设置当前用户ID用于代码保存和获取
          console.log('从本地存储设置用户ID:', userInfo.userId);
          setCurrentUserId(userInfo.userId);
          
          updateState({
            isAuthenticated: true,
            isLoading: false,
            user: userInfo,
            token: storedToken,
            error: null,
            lastChecked: Date.now()
          });
          return;
        } else {
          console.error('本地存储的token验证失败:', error);
          localStorage.removeItem(STORAGE_KEY);
        }
      }
      
      // 3. 未认证状态
      updateState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        token: null,
        error: null
      });
      
    } catch (error) {
      console.error('初始化认证状态失败:', error);
      updateState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        token: null,
        error: error instanceof Error ? error.message : '初始化认证状态失败'
      });
    }
  }, []);

  // 登录
  const login = async (token: string): Promise<void> => {
    try {
      updateState({ isLoading: true, error: null });
      
      const { isValid, userInfo, error } = await AuthService.validateToken(token);
      
      if (!isValid || !userInfo) {
        throw new Error(error || '无效的token');
      }
      
      // 保存到本地存储
      saveAuthToStorage(token, userInfo);
      
      // 更新状态
      updateState({
        isAuthenticated: true,
        isLoading: false,
        user: userInfo,
        token,
        error: null,
        lastChecked: Date.now()
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '登录失败';
      console.error('登录失败:', error);
      updateState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
      throw error;
    }
  };

  // 登出
  const logout = useCallback(async () => {
    try {
      if (state.token) {
        await AuthService.logout(state.token);
      }
    } catch (error) {
      console.error('登出时出错:', error);
    } finally {
      // 无论API调用是否成功，都清除本地状态
      localStorage.removeItem(STORAGE_KEY);
      updateState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        token: null,
        error: null
      });
    }
  }, [state.token]);

  // 刷新用户信息
  const refreshUserInfo = useCallback(async (): Promise<void> => {
    if (!state.token) {
      throw new Error('未找到认证token');
    }
    
    try {
      const { isValid, userInfo, error } = await AuthService.validateToken(state.token);
      
      if (!isValid || !userInfo) {
        throw new Error(error || '刷新用户信息失败');
      }
      
      updateState({ 
        user: userInfo,
        lastChecked: Date.now()
      });
    } catch (error) {
      console.error('刷新用户信息失败:', error);
      throw error;
    }
  }, [state.token, updateState]);

  // 组件挂载时初始化认证状态
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);
  
  // 设置定时刷新用户信息（每小时）
  useEffect(() => {
    if (!state.isAuthenticated) return;
    
    const refreshInterval = setInterval(() => {
      refreshUserInfo();
    }, 60 * 60 * 1000);
    
    return () => clearInterval(refreshInterval);
  }, [state.isAuthenticated, refreshUserInfo]);

  // 创建上下文值
  const contextValue: AuthContextType = {
    ...state,
    login,
    logout,
    initializeAuth,
    refreshUserInfo
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// 自定义hook，方便在函数组件中使用认证上下文
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth必须在AuthProvider内部使用');
  }
  return context;
};

// 高阶组件，用于类组件
export const withAuth = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<Omit<P, keyof AuthContextType>> => {
  const WithAuthComponent: React.FC<Omit<P, keyof AuthContextType>> = (props) => {
    const auth = useAuth();
    return <Component {...(props as P)} {...auth} />;
  };
  WithAuthComponent.displayName = `withAuth(${Component.displayName || Component.name || 'Component'})`;
  return WithAuthComponent;
};
